import { useOwnershipLabels } from "../../OwnershipLabels";
import { invalidOwnershipPercentageEdges, OWNERSHIP_PERCENTAGE_WARNING } from "../../../utils/ownershipPercentageValidation";
import { nameFields } from "../../../utils/individualName";
/* KycSectionOwnership.jsx */
import React, {
  useEffect,
  useMemo,
  useRef,
  useLayoutEffect,
  useState,
  useCallback,
} from "react";
import { createPortal } from "react-dom";
import "../css/KycSectionOwnership.css";
import { AnimatePresence, motion } from "framer-motion";

import CountrySelect from "../../../components/ui/CountrySelect";

import {
  scheduleBodyScrollUnlockCleanup,
  useLockBodyScroll,
} from "./common_utils/useLockBodyScroll";

import DirectShareholdersTable from "./KycSectionOwnershipComponents/DirectShareholdersTable";
import ControllingPersonsSection from "./KycSectionOwnershipComponents/ControllingPersonsSection";
import SignatoriesTable from "./KycSectionOwnershipComponents/SignatoriesTable";
import warningIcon from "../../../images/warning-b.svg";
import { getQualifiedUboNodeIds } from "../../../utils/qualifiedUboMarkers";

// ✅ UI only (assistant banner + snapshot + modal shell)
import OwnershipTreeUae, { OwnershipTreeUaeReadOnly } from "./OwnershipTreeUae";
import { editOwnershipPercentage } from "../../../utils/editOwnershipPercentage";
import { ownershipTreeWithMobileNodes } from "../../../utils/mobileAssistantTree";
import { ownershipKnownNodes } from "../../../utils/editOwnershipPercentage";
import { attachArrangementParties } from "../../../utils/uboAssessment";
import { builderSeedFromNodes, mergeEditedOwnership } from "../../../utils/ownershipEdit";
import { groupRemovedParties } from "../../../utils/ownershipPartyRemoval";
import useOwnershipRemoval from "../../hooks/useOwnershipRemoval";
import { isPrefilledRow } from "../../../utils/companyReadingPrefill";

/* -------------------- Helpers -------------------- */

function safeClone(obj) {
  try {
    if (typeof structuredClone === "function") return structuredClone(obj);
  } catch (_) {}
  return JSON.parse(JSON.stringify(obj ?? {}));
}

function makeRowId(prefix = "row") {
  return `${prefix}_${Date.now().toString(36)}_${Math.random()
    .toString(36)
    .slice(2, 10)}`;
}

function normalizeType(v) {
  return String(v || "")
    .trim()
    .toLowerCase();
}

function toNumberOrNull(v) {
  const s = String(v ?? "").trim();
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

function ownershipProfileFromAssistantTopType(initialAHType) {
  const raw = String(initialAHType || "").trim();
  const normalized = raw
    .toLowerCase()
    .replace(/[-_/]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  const compact = normalized.replace(/\s+/g, "");

  if (!raw) return { entityType: "", holdingKind: "" };
  if (compact === "pic" || normalized.includes("personal investment")) {
    return { entityType: "pic", holdingKind: "" };
  }
  if (compact === "holdingpassive" || normalized.includes("passive holding")) {
    return { entityType: "holding", holdingKind: "passive" };
  }
  if (compact === "holdingactive" || normalized.includes("active holding")) {
    return { entityType: "holding", holdingKind: "active" };
  }
  if (normalized === "trust") return { entityType: "trust", holdingKind: "" };
  if (normalized === "foundation") {
    return { entityType: "foundation", holdingKind: "" };
  }
  if (
    compact.includes("nonquoted") ||
    normalized.includes("operational") ||
    normalized.includes("operating")
  ) {
    return { entityType: "operating", holdingKind: "" };
  }
  if (normalized.includes("quoted")) return { entityType: "quoted", holdingKind: "" };

  return { entityType: "", holdingKind: "" };
}

// Handles both mobile ("quoted") and desktop ("Quoted Company") values
function isQuotedEntityType(s) {
  return /^quoted/i.test(String(s?.shareholderEntityType || "").trim());
}

function makeEmptyShareholderRow() {
  return {
    _rowId: makeRowId("sh"),
    name: "",
    holderType: "",
    beneficialOwnerType: "Beneficial owner",
    nationality: "",
    countryOfResidence: "",
    ownershipPercent: "",
  };
}

/* -------------------- Assistant type normalization -------------------- */

// Normalize any raw string (from payload, from snapshot node.type, etc.) -> Title Case expected by your UI
function normalizeHolderTypeTitleCase(raw) {
  const t = String(raw || "")
    .trim()
    .toLowerCase();
  if (t.includes("individual")) return "Individual";
  if (t.includes("trust")) return "Trust";
  if (t.includes("foundation")) return "Foundation";
  if (t.includes("entity")) return "Entity";
  // fallback
  return "Entity";
}

// If we have a snapshot, trust/foundation types MUST come from the tree node type (source of truth).
function holderTypeFromSnapshotOrPayload({ snapshot, treeNodeId, rawType }) {
  const snapNodes = Array.isArray(snapshot?.nodes) ? snapshot.nodes : [];
  if (treeNodeId && snapNodes.length) {
    const n = snapNodes.find((x) => String(x?.ID || "") === String(treeNodeId));
    const nodeType = String(n?.type || "").trim();
    if (nodeType) return normalizeHolderTypeTitleCase(nodeType);
  }
  return normalizeHolderTypeTitleCase(rawType);
}

/* -------------------- Legal Arrangements -------------------- */

function ensureLegalArrangementsShape(ownership) {
  const o = ownership && typeof ownership === "object" ? ownership : {};
  const la =
    o.legalArrangements && typeof o.legalArrangements === "object"
      ? o.legalArrangements
      : {};
  const arrangements = Array.isArray(la.arrangements) ? la.arrangements : [];
  return { arrangements };
}

function isCorporateKind(value) {
  const raw = String(value || "").trim().toLowerCase();
  return raw === "entity" || raw === "company" || raw === "corporate";
}

function normalizeOwnershipHolderType(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (raw === "entity") return "entity";
  if (raw === "trust") return "trust";
  if (raw === "foundation") return "foundation";
  if (raw === "legalarrangement" || raw === "legal_arrangement") {
    return "legalArrangement";
  }
  return "individual";
}

function isCorporateArrangementParty(party) {
  const role = String(party?.role || "").trim().toLowerCase();
  const kind = party?.trusteeKind || party?.councilKind;
  return (
    isCorporateKind(kind) &&
    (role === "trustee" ||
      role === "protector" ||
      role === "council member" ||
      role === "guardian")
  );
}

function corporateArrangementPartyMatchesRow(row, party) {
  const rowName = String(row?.name || "").trim().toLowerCase();
  const partyName = String(party?.name || "").trim().toLowerCase();
  if (!rowName || rowName !== partyName) return false;

  const rowCountry = String(row?.countryOfResidence || "").trim().toLowerCase();
  const partyCountry = String(
    party?.domicile || party?.countryOfIncorporation || ""
  )
    .trim()
    .toLowerCase();
  return !rowCountry || !partyCountry || rowCountry === partyCountry;
}

function isCorporateArrangementControlRow(row, legalArrangements = []) {
  const basis = String(row?.basisOfControl || "").toLowerCase();
  const basisLooksCorporate =
    (basis.includes("trust — trustee") ||
      basis.includes("trust — protector") ||
      basis.includes("foundation — council/board") ||
      basis.includes("foundation — guardian")) &&
    (basis.includes("(corporate)") ||
      basis.includes("(company)") ||
      basis.includes("(entity)"));
  if (basisLooksCorporate) return true;

  return (Array.isArray(legalArrangements) ? legalArrangements : []).some((arr) =>
    (Array.isArray(arr?.parties) ? arr.parties : []).some(
      (party) =>
        isCorporateArrangementParty(party) &&
        corporateArrangementPartyMatchesRow(row, party)
    )
  );
}

function isUboRowComplete(row, legalArrangements = []) {
  if (!String(row?.name || "").trim()) return false;
  if (!String(row?.countryOfResidence || "").trim()) return false;
  if (isCorporateArrangementControlRow(row, legalArrangements)) return true;
  return !!String(row?.nationality || "").trim();
}

function isArrangementPartyComplete(party, arrangementType) {
  const role = String(party?.role || "").trim().toLowerCase();
  const name = String(party?.name || "").trim();
  const nationality = String(party?.nationality || "").trim();
  const domicile = String(party?.domicile || "").trim();
  const kind = String(party?.trusteeKind || party?.councilKind || "")
    .trim()
    .toLowerCase();
  if (!name) return false;

  const type = normalizeArrangementType(arrangementType);
  const canBeCorporate =
    (type === "Trust" && role === "trustee") ||
    (type === "Foundation" && role === "council member");
  if (canBeCorporate) {
    if (!["individual", "entity", "company", "corporate"].includes(kind)) {
      return false;
    }
    if (isCorporateKind(kind)) return !!domicile;
  }

  return !!nationality && !!domicile;
}

function findShareholderArrangement(shareholder, legalArrangements = []) {
  const rowId = String(shareholder?._rowId || "").trim();
  const nodeId = String(shareholder?.treeNodeId || shareholder?.subjectNodeId || "").trim();
  const name = String(shareholder?.name || "").trim().toLowerCase();
  return (Array.isArray(legalArrangements) ? legalArrangements : []).find((arr) => {
    if (String(arr?.source || "") !== "shareholder") return false;
    if (rowId && String(arr?.sourceRefRowId || "").trim() === rowId) return true;
    if (
      nodeId &&
      (String(arr?.sourceRefNodeId || "").trim() === nodeId ||
        String(arr?._treeArrangementNodeId || "").trim() === nodeId)
    ) {
      return true;
    }
    return name && String(arr?.name || "").trim().toLowerCase() === name;
  });
}

function isLegalArrangementShareholderComplete(shareholder, legalArrangements = []) {
  const arrangement = findShareholderArrangement(shareholder, legalArrangements);
  if (!arrangement) return false;
  const parties = Array.isArray(arrangement?.parties) ? arrangement.parties : [];
  const hasIdentity =
    !!String(arrangement?.name || shareholder?.name || "").trim() ||
    parties.some(
      (party) =>
        String(party?.role || "").trim() === "Trustee" &&
        !!String(party?.name || "").trim()
    );
  return (
    hasIdentity &&
    parties.length > 0 &&
    parties.every((party) =>
      isArrangementPartyComplete(party, arrangement?.arrangementType)
    )
  );
}

function normalizeArrangementType(v) {
  const s = String(v || "")
    .trim()
    .toLowerCase();
  if (s === "foundation") return "Foundation";
  return "Trust";
}

function baseRolesForType(arrType) {
  const t = normalizeArrangementType(arrType);
  if (t === "Foundation") return ["Founder", "Council member", "Beneficiary"];
  return ["Settlor", "Trustee", "Beneficiary"];
}

function makeDefaultPartiesForType(arrType, { autoCreated = true } = {}) {
  const roles = baseRolesForType(arrType);
  return roles.map((role) => ({
    _rowId: makeRowId("laP"),
    role,
    name: "",
    trusteeKind: "",
    nationality: "",
    domicile: "",
    autoCreated,
  }));
}

function makeArrangement({
  type = "Trust",
  name = "",
  source = "manual", // shareholder | account_holder | manual | entity_manual
  sourceRefRowId = "",
  autoCreated = false,
  relatedShareholderName = "",
  parties = null, // ✅ allow injecting filled parties
} = {}) {
  const t = normalizeArrangementType(type);
  return {
    _rowId: makeRowId("la"),
    arrangementType: t,
    name: name || "",
    source,
    sourceRefRowId: sourceRefRowId || "",
    relatedShareholderName: relatedShareholderName || "",
    autoCreated: !!autoCreated,
    parties:
      Array.isArray(parties) && parties.length
        ? parties
        : makeDefaultPartiesForType(t, { autoCreated: !!autoCreated }),
    _autoName: name || "",
  };
}

function rebuildArrangementForType(arr, newType) {
  const cur = arr && typeof arr === "object" ? arr : {};
  const t = normalizeArrangementType(newType);
  return {
    ...cur,
    arrangementType: t,
    parties: makeDefaultPartiesForType(t, { autoCreated: !!cur.autoCreated }),
  };
}

function ubosSig(list) {
  return (Array.isArray(list) ? list : [])
    .map((u) =>
      [
        String(u?._rowId || ""),
        String(u?.relatedShareholderRowId || ""),
        String(u?.relatedShareholderNodeId || ""),
        String(u?.name || ""),
        String(u?.nationality || ""),
        String(u?.countryOfResidence || ""),
        String(u?.basisOfControl || ""),
        String(u?.effectiveOwnershipPercent || ""),
      ].join("|")
    )
    .join("~");
}

function arrangementsSig(list) {
  return (Array.isArray(list) ? list : [])
    .map((a) =>
      [
        String(a?._rowId || ""),
        String(a?.source || ""),
        String(a?.sourceRefRowId || ""),
        String(a?.sourceRefNodeId || ""),
        String(a?.arrangementType || ""),
        String(a?.name || ""),
        String(a?.relatedShareholderName || ""),
        a?.autoCreated ? "1" : "0",
        (Array.isArray(a?.parties) ? a.parties : [])
          .map((p) =>
            [
              String(p?._rowId || ""),
              String(p?.role || ""),
              String(p?.name || ""),
              String(p?.trusteeKind || ""),
              String(p?.nationality || ""),
              String(p?.domicile || ""),
            ].join(",")
          )
          .join(";"),
      ].join("|")
    )
    .join("~");
}

function normalizeTrustNameResolution(v) {
  const s = String(v || "")
    .trim()
    .toLowerCase();
  return s === "trust" || s === "trustee" ? s : "";
}

function getAccountHolderTrustResolution(ownership) {
  const raw =
    ownership && typeof ownership === "object"
      ? ownership.accountHolderTrustResolution
      : null;
  return {
    kind: normalizeTrustNameResolution(raw?.kind),
    sourceName: String(raw?.sourceName || "").trim(),
  };
}

function applyAccountHolderTrustResolutionToArrangement(
  arrangement,
  resolution,
  sourceName
) {
  const next = arrangement && typeof arrangement === "object" ? { ...arrangement } : {};
  const resolvedName = String(sourceName || "").trim();
  const resolutionKind = normalizeTrustNameResolution(resolution?.kind);

  if (normalizeArrangementType(next?.arrangementType) !== "Trust") return next;

  const parties = Array.isArray(next?.parties)
    ? next.parties.map((p) => ({ ...(p || {}) }))
    : makeDefaultPartiesForType("Trust", {
        autoCreated: !!next?.autoCreated,
      });

  const trusteeIdx = parties.findIndex(
    (p) => String(p?.role || "").trim() === "Trustee"
  );

  if (resolutionKind === "trust") {
    const prevAutoTrustName = String(next?._autoAccountHolderTrustName || "");
    const currentName = String(next?.name || "");
    if (!currentName || currentName === prevAutoTrustName) {
      next.name = resolvedName;
    }
    next._autoAccountHolderTrustName = resolvedName;

    if (trusteeIdx !== -1) {
      const trustee = { ...(parties[trusteeIdx] || {}) };
      const prevAutoTrusteeName = String(
        trustee?._autoAccountHolderTrusteeName || ""
      );
      const currentTrusteeName = String(trustee?.name || "");
      if (
        prevAutoTrusteeName &&
        (!currentTrusteeName || currentTrusteeName === prevAutoTrusteeName)
      ) {
        trustee.name = "";
      }
      trustee._autoAccountHolderTrusteeName = "";
      parties[trusteeIdx] = trustee;
    }
  } else if (resolutionKind === "trustee") {
    const prevAutoTrustName = String(next?._autoAccountHolderTrustName || "");
    const currentName = String(next?.name || "");
    if (prevAutoTrustName && (!currentName || currentName === prevAutoTrustName)) {
      next.name = "";
    }
    next._autoAccountHolderTrustName = "";

    if (trusteeIdx !== -1) {
      const trustee = { ...(parties[trusteeIdx] || {}) };
      const prevAutoTrusteeName = String(
        trustee?._autoAccountHolderTrusteeName || ""
      );
      const currentTrusteeName = String(trustee?.name || "");
      if (!currentTrusteeName || currentTrusteeName === prevAutoTrusteeName) {
        trustee.name = resolvedName;
      }
      trustee._autoAccountHolderTrusteeName = resolvedName;
      parties[trusteeIdx] = trustee;
    }
  }

  next.parties = parties;
  next._accountHolderTrustResolutionKind = resolutionKind;
  next._accountHolderTrustResolutionSourceName = resolvedName;
  return next;
}

function applyAccountHolderFoundationNameToArrangement(arrangement, sourceName) {
  const next =
    arrangement && typeof arrangement === "object" ? { ...arrangement } : {};
  const resolvedName = String(sourceName || "").trim();

  if (normalizeArrangementType(next?.arrangementType) !== "Foundation")
    return next;

  const prevAutoFoundationName = String(
    next?._autoAccountHolderFoundationName || ""
  );
  const currentName = String(next?.name || "");
  if (!currentName || currentName === prevAutoFoundationName) {
    next.name = resolvedName;
  }

  next._autoAccountHolderFoundationName = resolvedName;
  return next;
}

function clearAccountHolderTrustResolutionFromArrangement(arrangement) {
  const next =
    arrangement && typeof arrangement === "object" ? { ...arrangement } : {};
  const parties = Array.isArray(next?.parties)
    ? next.parties.map((p) => ({ ...(p || {}) }))
    : [];

  next._autoAccountHolderTrustName = "";
  next._accountHolderTrustResolutionKind = "";
  next._accountHolderTrustResolutionSourceName = "";

  const trusteeIdx = parties.findIndex(
    (p) => String(p?.role || "").trim() === "Trustee"
  );
  if (trusteeIdx !== -1) {
    const trustee = { ...(parties[trusteeIdx] || {}) };
    trustee._autoAccountHolderTrusteeName = "";
    parties[trusteeIdx] = trustee;
  }

  next.parties = parties;
  return next;
}

/* -------------------- Assistant mapping helpers -------------------- */

function mapAssistantShareholdersToFormRows(assistantShareholders) {
  const list = Array.isArray(assistantShareholders)
    ? assistantShareholders
    : [];

  return list.map((s) => {
    const entType = String(s?.shareholderEntityType || s?.holderType || "")
      .trim()
      .toLowerCase();

    // ✅ KYC form expects holderType to be one of: individual | entity | trust | foundation
    let holderType = "Entity";
    if (entType === "individual") holderType = "Individual";
    else if (entType === "trust") holderType = "Trust";
    else if (entType === "foundation") holderType = "Foundation";
    else holderType = "Entity";

    return {
      _rowId: String(s?._rowId || "") || makeRowId("sh"),
      treeNodeId: String(s?.treeNodeId || ""),
      ...nameFields(s),
      name: String(s?.name || ""),
      holderType,
      beneficialOwnerType: String(s?.beneficialOwnerType || "Beneficial owner"),
      nationality: String(s?.nationality || ""),
      countryOfResidence: String(s?.countryOfResidence || ""),
      ownershipPercent: String(s?.ownershipPercent || ""),
      shareholderEntityType: String(s?.shareholderEntityType || ""),
	          isPIC: !!s?.isPIC,
	          isHoldingPassive: !!s?.isHoldingPassive,
	          isHoldingActive: !!s?.isHoldingActive,
	          isPicOrPassiveHolding: !!s?.isPicOrPassiveHolding,
	          requiresDocuments: s?.requiresDocuments ?? null,
	        };
	      });
}

function isRoleBasedUboRow(u) {
  const basis = String(u?.basisOfControl || "")
    .trim()
    .toLowerCase();
  return basis.startsWith("trust —") || basis.startsWith("foundation —");
}

export function buildPartiesFromTreeRoles(arrType, roles) {
  const t = normalizeArrangementType(arrType);

  const mk = (role, nameOrPerson) => ({
    _rowId: makeRowId("laP"),
    role,
    ...nameFields(typeof nameOrPerson === "object" ? nameOrPerson : {}),
    name: String(typeof nameOrPerson === "object" ? nameOrPerson?.name || "" : nameOrPerson || "").trim(),
    trusteeKind: typeof nameOrPerson === "object" ? String(nameOrPerson?.kind || "") : "",
    // Details entered in the builder for this party (a company party has a country of incorporation).
    nationality: typeof nameOrPerson === "object" ? String(nameOrPerson?.nationality || "") : "",
    domicile: typeof nameOrPerson === "object"
      ? String(nameOrPerson?.domicile || nameOrPerson?.countryOfIncorporation || "")
      : "",
    ...(typeof nameOrPerson === "object" && (nameOrPerson?.dateOfBirth || nameOrPerson?.incorporationDate)
      ? { dobOrIncorporationDate: nameOrPerson.dateOfBirth || nameOrPerson.incorporationDate }
      : {}),
    autoCreated: true,
  });

  if (!roles || typeof roles !== "object") {
    return makeDefaultPartiesForType(t, { autoCreated: true });
  }

  if (t === "Trust" && roles.kind === "trust") {
    const out = [];
    out.push(mk("Settlor", roles?.settlor));
    out.push({
      ...mk("Trustee", roles?.trustee),
      trusteeKind: String(roles?.trustee?.kind || "").trim(),
    });

    if (roles?.beneficiaries?.kind === "named") {
      (roles?.beneficiaries?.list || []).forEach((b) => {
        out.push(mk("Beneficiary", b));
      });
    } else if (roles?.beneficiaries?.kind === "class") {
      out.push(mk("Beneficiary", "Class (unnamed)"));
    } else {
      out.push(mk("Beneficiary", ""));
    }

    if (roles?.protector?.name) {
      out.push(mk("Protector", roles?.protector));
    }

    const nonEmpty = out.filter((p) => String(p.name || "").trim());
    return nonEmpty.length ? nonEmpty : out;
  }

  if (t === "Foundation" && roles.kind === "foundation") {
    const out = [];
    out.push(mk("Founder", roles?.founder));
    out.push(mk("Council/Board", roles?.council));

    if (roles?.beneficiaries?.kind === "named") {
      (roles?.beneficiaries?.list || []).forEach((b) => {
        out.push(mk("Beneficiary", b));
      });
    } else if (roles?.beneficiaries?.kind === "class") {
      out.push(mk("Beneficiary", "Class (unnamed)"));
    } else {
      out.push(mk("Beneficiary", ""));
    }

    if (roles?.guardian?.name) {
      out.push(mk("Guardian", roles?.guardian));
    }

    const nonEmpty = out.filter((p) => String(p.name || "").trim());
    return nonEmpty.length ? nonEmpty : out;
  }

  return makeDefaultPartiesForType(t, { autoCreated: true });
}

// A trust/foundation drawn in the diagram is identified by its box; same-name arrangements
// in different boxes stay separate. Rows without a box keep the previous name-based key.
export function arrangementRowKey(arr) {
  const nodeId = String(arr?._treeArrangementNodeId || "").trim();
  if (nodeId) return `node:${nodeId}`;
  return `${String(arr?.arrangementType || "")}|${String(arr?.name || "")}|${String(
    arr?.sourceRefRowId || ""
  )}|${String(arr?.source || "")}`;
}

// The client-type mode whose change resets the ownership rows.
function ownershipModeKey(flags) {
  if (flags?.quotedCompany) return "quoted";
  if (flags?.accountHolderTrust) return "ah_trust";
  if (flags?.accountHolderFoundation) return "ah_foundation";
  if (flags?.pctNotMeaningful) return "pic";
  return "default";
}

const OWNERSHIP_MIRROR_KEYS = {
  shareholders: "directShareholders",
  ubos: "controllingPersonsNatural",
  legalArrangements: "controllingPersonsLegalArrangements",
};

function buildLegalArrangementsFromSnapshot({
  snapshot,
  entityName,
  shareholdersFormRows,
}) {
  const s = snapshot && typeof snapshot === "object" ? snapshot : {};
  const nodes = Array.isArray(s?.nodes) ? s.nodes : [];
  const initialAHType = String(s?.initialAHType || "").trim();

  const nodeById = new Map(nodes.map((n) => [String(n?.ID || ""), n]));

  const shByTreeNodeId = new Map();
  (shareholdersFormRows || []).forEach((sh) => {
    const tid = String(sh?.treeNodeId || "").trim();
    if (tid) shByTreeNodeId.set(tid, sh);
  });

  const arrangements = [];

  function directShareholderNodeIdForSnapshotNode(node) {
    const nodeId = String(node?.ID || "").trim();
    const parents = Array.isArray(node?.parentIDs) ? node.parentIDs.map((id) => String(id || "")) : [];
    if (parents[0] === "X0") return nodeId;
    const x0Index = parents.indexOf("X0");
    if (x0Index > 0) return parents[x0Index - 1];
    return "";
  }

  if (initialAHType === "Trust" || initialAHType === "Foundation") {
    const x0 = nodeById.get("X0");
    const arrType = initialAHType;
    arrangements.push(
      makeArrangement({
        type: arrType,
        ...nameFields(x0),
        name: String(x0?.name || "").trim() || entityName,
        source: "account_holder",
        sourceRefRowId: "",
        autoCreated: true,
        relatedShareholderName: entityName,
        parties: buildPartiesFromTreeRoles(arrType, x0?.roles),
      })
    );
  }

  nodes.forEach((n) => {
    if (String(n?.ID || "") === "X0") return;
    const t = String(n?.type || "").trim();
    if (!(t === "Trust" || t === "Foundation")) return;

    const treeNodeId = String(n?.ID || "").trim();
    const directTreeNodeId = directShareholderNodeIdForSnapshotNode(n);
    const shRow = shByTreeNodeId.get(directTreeNodeId || treeNodeId);

    arrangements.push(
      {
        ...makeArrangement({
        type: t,
        ...nameFields(n),
        name: String(n?.name || "").trim(),
        source: "shareholder",
        sourceRefRowId: String(shRow?._rowId || ""),
        autoCreated: true,
        relatedShareholderName:
          String(shRow?.name || "").trim() || String(entityName || "").trim(),
        parties: buildPartiesFromTreeRoles(t, n?.roles),
        }),
        _treeArrangementNodeId: treeNodeId,
      }
    );
  });

  const seen = new Set();
  const out = [];
  arrangements.forEach((a) => {
    const key = arrangementRowKey(a);
    if (seen.has(key)) return;
    seen.add(key);
    out.push(a);
  });

  return out;
}

/* -------------------- Component -------------------- */

export default function KycSectionOwnership({
  sectionNo = 0,
  separateNames = false,
  form,
  orgJurisdiction = null,
  onSetAt,
  mode = "staff",
  changedClass,
  changedRows,
  readOnly = false,
  copyOverrides = {},

  showDirectShareholders = true,
  showSignatories = true,

  hideOwnershipAssistantIntro = false,
  hidePreliminaryInformation = false,
  hideAssistantSnapshot = false,
  hideControllingPersonsDivider = false,
  hideControllingPersonsHeadings = false,
  disableAutoEmptyShareholder = false,
  assistantInitialAccountHolderType = "",
  assistantInitialAccountHolderTrustNameKind = "",
  autoOpenAssistantKey = "",
  onAssistantApplied,
  onAssistantCloseIncomplete,
  onAssistantOpenChange,
  embedded = false,
  submitAttempted = false,
  openAssistantTrigger = 0,
  noModeReset = false,
  // Changing this value keeps the current rows on the next client-type change ("keep structure").
  keepRowsOnModeChangeToken = 0,
  // Client e-form (kyc-sent): client wording in the Edit diagram removal dialog.
  clientFacing = false,
  // A note shown above the direct shareholders (e.g. percentages now needed after a type change).
  shareholdersNote = "",
  // Changing this value opens the builder on the current structure ("Edit diagram").
  openEditTrigger = 0,
  forceApiCallInTree = false,
  assistantSnapshotOverride = null,
  assistantSnapshotWarning = "",
  onHowItWorks,
}) {
  const ownership = form?.ownership || {};
  const uboFlags = ownership?.uboFlags || {};
  const qualifiedUboNodeIds = useMemo(
    () => getQualifiedUboNodeIds(ownership),
    [ownership]
  );

  const entityNameRaw = String(form?.customer?.name || "").trim();
  const entityName = entityNameRaw || "Account holder";

  const shareholders = Array.isArray(ownership.shareholders)
    ? ownership.shareholders
    : [];
  const ubos = Array.isArray(ownership.ubos) ? ownership.ubos : [];
  const signatories = Array.isArray(ownership.signatories)
    ? ownership.signatories
    : [];

  const legalArr = ensureLegalArrangementsShape(ownership);
  const legalArrangements = Array.isArray(legalArr.arrangements)
    ? legalArr.arrangements
    : [];

  const cc = (path) =>
    typeof changedClass === "function" ? changedClass(path) : "";

  const { ownershipLabel } = useOwnershipLabels(orgJurisdiction);
  const isPIC = !!uboFlags.pctNotMeaningful;
  const isQuoted = !!uboFlags.quotedCompany;
  const isAccountHolderTrust = !!uboFlags.accountHolderTrust;
  const isAccountHolderFoundation = !!uboFlags.accountHolderFoundation;

  const accountHolderIsTF = isAccountHolderTrust || isAccountHolderFoundation;
  const accountHolderTFType = isAccountHolderFoundation
    ? "Foundation"
    : "Trust";
  const accountHolderTrustResolution =
    getAccountHolderTrustResolution(ownership);
  const hasResolvedAccountHolderTrustName = !!accountHolderTrustResolution.kind;
  const needsAccountHolderTrustResolution =
    isAccountHolderTrust &&
    !readOnly &&
    !!entityNameRaw &&
    !accountHolderTrustResolution.kind;

  const hasOtherControl = !!uboFlags.hasOtherControl;

  /* -------------------- Copy -------------------- */

  const COPY = {
    staff: {
      header: { kicker: null, title: " | Ownership & Control", hint: null },
    },
    client: {
      header: {
        kicker: "Section 2",
        title: " | Ownership & Control",
        hint: "Please list direct shareholders and authorised signatories. Controlling persons will appear when required",
      },
    },
    review: {
      header: { kicker: null, title: " | Ownership & Control", hint: null },
    },
  };

  const baseCopy = COPY[mode] || COPY.staff;
  const copy = {
    ...baseCopy,
    ...copyOverrides,
    header: { ...baseCopy.header, ...(copyOverrides.header || {}) },
  };

  /* =========================================================
           OWNERSHIP ASSISTANT (UI ONLY)
        ========================================================= */

  const [ownershipAssistantOpen, setOwnershipAssistantOpen] = useState(!!autoOpenAssistantKey);
  // Builder opened on the current structure (Edit diagram); null when building from scratch.
  const [editSeed, setEditSeed] = useState(null);
  // Company documents with a company among the shareholders (kyc-sent): a builder started from scratch, on an
  // ownership still empty, begins with the direct shareholders read (ownership.documentShareholders, see companyReadingPrefill).
  const documentShareholders = !editSeed &&
    ![ownership?.shareholders, ownership?.directShareholders].some((list) => Array.isArray(list) && list.some((row) => String(row?.name || "").trim()))
    ? ownership?.documentShareholders || null : null;
  const { requestRemoval, removalDialog } = useOwnershipRemoval(form);
  const [ownershipAssistantContentReady, setOwnershipAssistantContentReady] =
    useState(false);
  useLockBodyScroll(ownershipAssistantOpen, { strategy: "overflow" });
  const assistantAppliedRef = useRef(false);

  useEffect(() => {
    onAssistantOpenChange?.(ownershipAssistantOpen);
    return () => onAssistantOpenChange?.(false);
  }, [onAssistantOpenChange, ownershipAssistantOpen]);

  useEffect(() => {
    if (!ownershipAssistantOpen) {
      setOwnershipAssistantContentReady(false);
      return undefined;
    }

    setOwnershipAssistantContentReady(false);
    const timer = window.setTimeout(() => {
      setOwnershipAssistantContentReady(true);
    }, 90);

    return () => {
      window.clearTimeout(timer);
    };
  }, [ownershipAssistantOpen]);

  const prevAutoOpenAssistantKeyRef = useRef(autoOpenAssistantKey || "");

  useLayoutEffect(() => {
    if (readOnly) return;
    if (!autoOpenAssistantKey) return;
    if (prevAutoOpenAssistantKeyRef.current === autoOpenAssistantKey) return;
    prevAutoOpenAssistantKeyRef.current = autoOpenAssistantKey;
    assistantAppliedRef.current = false;
    setOwnershipAssistantOpen(true);
  }, [autoOpenAssistantKey, readOnly]);

  const prevOpenAssistantTriggerRef = useRef(0);
  useEffect(() => {
    if (readOnly) return;
    if (!openAssistantTrigger) return;
    if (prevOpenAssistantTriggerRef.current === openAssistantTrigger) return;
    prevOpenAssistantTriggerRef.current = openAssistantTrigger;
    assistantAppliedRef.current = false;
    setOwnershipAssistantOpen(true);
  }, [openAssistantTrigger, readOnly]);

  const openOwnershipAssistant = () => {
    if (readOnly) return;
    assistantAppliedRef.current = false;
    setOwnershipAssistantOpen(true);
  };

  const closeOwnershipAssistant = () => {
    if (!assistantAppliedRef.current && !editSeed) {
      onAssistantCloseIncomplete?.();
    }
    setOwnershipAssistantOpen(false);
    setEditSeed(null);
  };

  /* =========================================================
           APPLY UPDATE FROM MODAL (AUTO-FILL KYC FORM)
        ========================================================= */

  // ✅ prevents the mode-switch reset effect from wiping assistant-written rows
  const skipNextModeResetRef = useRef(false);

  const applyAssistantUpdate = useCallback(
    (payload) => {
      if (readOnly) return;

      // ✅ IMPORTANT: the assistant sets flags => modeKey changes => your mode reset effect runs.
      // This prevents that effect from clearing the just-written arrays.
      assistantAppliedRef.current = true;
      onAssistantApplied?.();

      const p = payload && typeof payload === "object" ? payload : {};

      // snapshot from modal (read-only diagram)
      const snapshot =
        p.assistantTree && typeof p.assistantTree === "object"
          ? p.assistantTree
          : null;

      const initialAHType = String(
        snapshot?.initialAHType || p?.initialAHType || ""
      ).trim();
      const nextProfile = ownershipProfileFromAssistantTopType(initialAHType);

      // ---------------------------
      // 1) FLAGS (checkboxes)
      // ---------------------------
      const nextFlags = safeClone(uboFlags);

      nextFlags.pctNotMeaningful = false;
      nextFlags.quotedCompany = false;
      nextFlags.accountHolderTrust = false;
      nextFlags.accountHolderFoundation = false;

      nextFlags.pctNotMeaningful = !!p?.controlFlags?.pctNotMeaningful;
      nextFlags.quotedCompany = !!p?.controlFlags?.quotedCompany;

      if (nextProfile.entityType) {
        nextFlags.pctNotMeaningful =
          nextProfile.entityType === "pic" ||
          (nextProfile.entityType === "holding" &&
            nextProfile.holdingKind === "passive");
        nextFlags.quotedCompany = nextProfile.entityType === "quoted";
      }

      if (initialAHType === "Trust") nextFlags.accountHolderTrust = true;
      if (initialAHType === "Foundation")
        nextFlags.accountHolderFoundation = true;

      if (typeof p?.controlOtherThanShareholding === "boolean") {
        nextFlags.hasOtherControl = !!p.controlOtherThanShareholding;
      }

      // ✅ IMPORTANT: when the assistant changes the client type, modeKey changes and the mode
      // reset effect would clear the just-written rows: skip that one reset. Only then — a skip
      // left pending (same type) would swallow the user's next real type change (e.g. to PIC).
      skipNextModeResetRef.current = ownershipModeKey(nextFlags) !== ownershipModeKey(uboFlags);

      onSetAt?.(["ownership", "uboFlags"], nextFlags);

      if (nextProfile.entityType) {
        onSetAt?.(["ownership", "desktopClientEntityType"], nextProfile.entityType);
        onSetAt?.(["ownership", "desktopClientHoldingKind"], nextProfile.holdingKind);
      }

      if (
        p?.accountHolderTrustResolution &&
        typeof p.accountHolderTrustResolution === "object"
      ) {
        onSetAt?.(
          ["ownership", "accountHolderTrustResolution"],
          p.accountHolderTrustResolution
        );
      } else if (initialAHType !== "Trust") {
        onSetAt?.(["ownership", "accountHolderTrustResolution"], null);
      }

      // ---------------------------
      // 2) snapshot
      // ---------------------------
      if (Object.prototype.hasOwnProperty.call(p, "assistantTree")) {
        onSetAt?.(["ownership", "assistantTree"], snapshot);
      }

      // ---------------------------
      // 3) Direct shareholders rows
      // ---------------------------
      const srcShareholders = Array.isArray(p?.shareholders)
        ? p.shareholders
        : [];

      // ✅ FIX: holderType comes from snapshot node.type when available (trust/foundation direct BO)
      const nextShareholders = srcShareholders.map((s) => {
        const rawType = String(
          s?.holderType || s?.shareholderEntityType || "entity"
        )
          .trim()
          .toLowerCase();

        const treeNodeId = String(s?.treeNodeId || "").trim();

        const holderType = holderTypeFromSnapshotOrPayload({
          snapshot,
          treeNodeId,
          rawType,
        });

        return {
          _rowId: String(s?._rowId || "") || makeRowId("sh"),
          treeNodeId,
          ...nameFields(s),
          name: String(s?.name || "").trim(),
          holderType,

          beneficialOwnerType: String(
            s?.beneficialOwnerType || "Beneficial owner"
          ),
          nationality: String(s?.nationality || ""),
          countryOfResidence: String(s?.countryOfResidence || ""),
          ownershipPercent: String(s?.ownershipPercent || ""),

          shareholderEntityType: String(s?.shareholderEntityType || ""),
          isPIC: !!s?.isPIC,
          isHoldingPassive: !!s?.isHoldingPassive,
          isHoldingActive: !!s?.isHoldingActive,
          isPicOrPassiveHolding: !!s?.isPicOrPassiveHolding,
          // "Documents required" from the builder's UBO check (null = not assessed yet).
          ...(s?.requiresDocuments !== undefined ? { requiresDocuments: s.requiresDocuments } : {}),
        };
      });

      // ---------------------------
      // 4) Controlling persons (natural persons)
      // ---------------------------
      const srcUbos = Array.isArray(p?.ubos) ? p.ubos : [];

      const nextNaturalPersons = srcUbos
        .filter((u) => {
          const b = String(u?.basisOfControl || "")
            .trim()
            .toLowerCase();
          return !(b.startsWith("trust —") || b.startsWith("foundation —"));
        })
        .map((u) => ({
          _rowId: String(u?._rowId || "") || makeRowId("ubo"),

          relatedShareholderIdx: "",
          relatedShareholderNodeId: String(u?.relatedShareholderNodeId || ""),
          relatedShareholderRowId: String(u?.relatedShareholderRowId || ""),

          ...nameFields(u),

          name: String(u?.name || "").trim(),
          nationality: String(u?.nationality || ""),
          countryOfResidence: String(u?.countryOfResidence || ""),

          basisOfControl: String(u?.basisOfControl || ""),
          effectiveOwnershipPercent: String(u?.effectiveOwnershipPercent || ""),

          ownershipPercent: String(u?.effectiveOwnershipPercent || ""),
          meetsThreshold: u?.meetsThreshold ?? null,
          treeNodeId: String(u?.treeNodeId || ""),
        }));

      // ---------------------------
      // 5) Legal arrangements (Trust/Foundation roles)
      // ---------------------------
      const payloadArrangements = Array.isArray(p?.arrangements)
        ? p.arrangements
        : [];
      const snapshotArrangements = snapshot
        ? buildLegalArrangementsFromSnapshot({
            snapshot,
            entityName,
            shareholdersFormRows: nextShareholders,
          })
        : [];
      const arrangementSeen = new Set();
      const nextArrangements = [
        ...payloadArrangements,
        ...snapshotArrangements,
      ].filter((arr) => {
        const key = arrangementRowKey(arr);
        if (arrangementSeen.has(key)) return false;
        arrangementSeen.add(key);
        return true;
      });

      // ---------------------------
      // 6) WRITE (dual-write to match your UI paths)
      // ---------------------------

      onSetAt?.(["ownership", "shareholders"], nextShareholders);
      onSetAt?.(["ownership", "directShareholders"], nextShareholders);

      onSetAt?.(["ownership", "ubos"], nextNaturalPersons);
      onSetAt?.(["ownership", "controllingPersonsNatural"], nextNaturalPersons);

      onSetAt?.(["ownership", "legalArrangements"], {
        arrangements: nextArrangements,
      });
      onSetAt?.(["ownership", "controllingPersonsLegalArrangements"], {
        arrangements: nextArrangements,
      });

      scheduleBodyScrollUnlockCleanup();
    },
    [readOnly, onSetAt, uboFlags, entityName]
  );

  /* =========================================================
           EDIT DIAGRAM: builder opened on the current structure, merged on apply
        ========================================================= */

  function openEditDiagram(snapshot) {
    if (readOnly || !snapshot?.nodes?.length) return;
    const nodes = attachArrangementParties(snapshot.nodes, ownership?.legalArrangements?.arrangements, {
      shareholders: Array.isArray(ownership?.shareholders) ? ownership.shareholders : ownership?.directShareholders,
      knownNodes: ownershipKnownNodes(ownership),
    });
    const seed = builderSeedFromNodes(nodes);
    if (!seed) return;
    assistantAppliedRef.current = false;
    setEditSeed(seed);
    setOwnershipAssistantOpen(true);
  }

  // Apply from the builder: merged into the existing rows (kept by box), removals confirmed when
  // the removed parties have approved documents or linked signatories. Returning false keeps the
  // builder open until the merge is confirmed ("Back to the diagram" returns to it).
  function applyBuilderUpdate(built) {
    if (!editSeed) return applyAssistantUpdate(built);
    if (readOnly) return false;
    const { ownership: merged, removed } = mergeEditedOwnership(ownership, built, editSeed);
    requestRemoval(groupRemovedParties(removed), {
      mode: "edit",
      ...(clientFacing ? {
        title: "Save these changes?",
        intro: "You removed:",
        applyLabel: "Save changes",
        backLabel: "Back to the structure",
        signatoriesTitle: `These people can also sign for ${String(form?.customer?.name || "").trim() || "the company"}:`,
        kindLabels: { individual: "Person", company: "Company", trust: "Trust", foundation: "Foundation" },
      } : {}),
      onConfirmed: ({ signatories }) => {
        onSetAt?.(["ownership"], {
          ...merged,
          ...(Array.isArray(ownership?.signatories) || signatories.length ? { signatories } : {}),
        });
        assistantAppliedRef.current = true;
        onAssistantApplied?.();
        setOwnershipAssistantOpen(false);
        setEditSeed(null);
      },
    });
    return false;
  }

  // Edit requested by the parent (e.g. the staff review page's own diagram).
  const prevOpenEditTriggerRef = useRef(openEditTrigger);
  useEffect(() => {
    if (prevOpenEditTriggerRef.current === openEditTrigger) return;
    prevOpenEditTriggerRef.current = openEditTrigger;
    if (openEditTrigger) openEditDiagram(assistantSnapshotOverride?.mermaid ? assistantSnapshotOverride : ownership?.assistantTree);
    // eslint-disable-next-line
  }, [openEditTrigger]);

  // "Keep the ownership structure" on a client-type change: skip the next mode reset.
  const prevKeepRowsTokenRef = useRef(keepRowsOnModeChangeToken);
  useLayoutEffect(() => {
    if (prevKeepRowsTokenRef.current === keepRowsOnModeChangeToken) return;
    prevKeepRowsTokenRef.current = keepRowsOnModeChangeToken;
    if (keepRowsOnModeChangeToken) skipNextModeResetRef.current = true;
  }, [keepRowsOnModeChangeToken]);

  /* =========================================================
           MODE KEY (global reset on explicit mode switches)
        ========================================================= */

  const modeKey = useMemo(() => ownershipModeKey(uboFlags), [uboFlags]);

  const prevModeKeyRef = useRef(modeKey);

  useEffect(() => {
    if (readOnly) return;

    const prev = prevModeKeyRef.current;
    if (prev === modeKey) return;
    prevModeKeyRef.current = modeKey;

    if (noModeReset) return;

    if (skipNextModeResetRef.current) {
      skipNextModeResetRef.current = false;
      return;
    }

    onSetAt?.(["ownership", "shareholders"], []);
    onSetAt?.(["ownership", "ubos"], []);
    onSetAt?.(["ownership", "legalArrangements"], { arrangements: [] });

    if (!disableAutoEmptyShareholder && (modeKey === "default" || modeKey === "pic")) {
      onSetAt?.(["ownership", "shareholders"], [makeEmptyShareholderRow()]);
    }

    const flags = safeClone(uboFlags);
    flags.hasOtherControl = false;
    onSetAt?.(["ownership", "uboFlags"], flags);
    // eslint-disable-next-line
  }, [readOnly, modeKey, noModeReset]);

  useLayoutEffect(() => {
    if (readOnly) return;
    if (noModeReset) return;
    if (disableAutoEmptyShareholder) return;
    if (!(modeKey === "default" || modeKey === "pic")) return;
    if ((shareholders || []).length > 0) return;
    onSetAt?.(["ownership", "shareholders"], [makeEmptyShareholderRow()]);
  }, [readOnly, noModeReset, disableAutoEmptyShareholder, modeKey, shareholders.length]);

  /* =========================================================
           SHAREHOLDER SNAPSHOT (drives sync engine)
        ========================================================= */

  const shareholderSignature = useMemo(() => {
    return (shareholders || [])
      .map((s, idx) => {
        const rid = String(s?._rowId || `missing_${idx}`);
        const t = normalizeType(s?.holderType);
        const pct = String(s?.ownershipPercent ?? "");
        const nm = String(s?.name ?? "");
        return `${rid}:${t}:${pct}:${nm}`;
      })
      .join("|");
  }, [shareholders]);

  const shareholderModel = useMemo(() => {
    const byId = new Map();
    const rowIdByTreeNodeId = new Map();
    (shareholders || []).forEach((s, idx) => {
      const id = String(s?._rowId || `sh_${idx}`);
      const treeNodeId = String(s?.treeNodeId || "").trim();
      const type = normalizeType(s?.holderType);
      const name = String(s?.name || "").trim();
      const pct = toNumberOrNull(s?.ownershipPercent);
      if (id) byId.set(id, { type, name, pct });
      if (treeNodeId && id) rowIdByTreeNodeId.set(treeNodeId, id);
    });

    const eligibleEntityIds = new Set();
    const entityNameById = new Map();
    (shareholders || []).forEach((s, idx) => {
      const id = String(s?._rowId || `sh_${idx}`);
      const type = normalizeType(s?.holderType);
      if (type !== "entity") return;

      const name = String(s?.name || "").trim();
      eligibleEntityIds.add(id);
      entityNameById.set(id, name);
    });

    const eligibleTF = new Map();
    const tfNameById = new Map();
    (shareholders || []).forEach((s, idx) => {
      const id = String(s?._rowId || `sh_${idx}`);
      const type = normalizeType(s?.holderType);
      if (!(type === "trust" || type === "foundation")) return;

      const name = String(s?.name || "").trim();
      const targetType = type === "foundation" ? "Foundation" : "Trust";
      eligibleTF.set(id, targetType);
      tfNameById.set(id, name);
    });

    function directShareholderNodeIdForSnapshotNode(node) {
      const nodeId = String(node?.ID || "").trim();
      const parents = Array.isArray(node?.parentIDs)
        ? node.parentIDs.map((id) => String(id || ""))
        : [];
      if (parents[0] === "X0") return nodeId;
      const x0Index = parents.indexOf("X0");
      if (x0Index > 0) return parents[x0Index - 1];
      return "";
    }

    const nestedEntityOptions = [];
    // Mobile-assistant structures have no saved boxes: they are built from the structure.
    const savedTree = ownershipTreeWithMobileNodes(ownership);
    const snapshotNodes = Array.isArray(savedTree?.nodes) ? savedTree.nodes : [];
    const directShareholderNodeIds = new Set(rowIdByTreeNodeId.keys());
    const entityLikeTypes = new Set([
      "entity",
      "company",
      "non quoted operational company",
      "non quoted operating company",
      "active holding company",
      "passive holding company",
      // Tree node types used by the builder and assistants for holdings and operating companies.
      "holdingactive",
      "holdingpassive",
      "operating company",
      "quoted company",
      "pic",
    ]);

    snapshotNodes.forEach((node) => {
      const nodeId = String(node?.ID || "").trim();
      if (!nodeId || nodeId === "X0") return;
      if (directShareholderNodeIds.has(nodeId)) return;

      const type = String(node?.type || "").trim().toLowerCase();
      if (!entityLikeTypes.has(type)) return;

      const directNodeId = directShareholderNodeIdForSnapshotNode(node);
      const directRowId = rowIdByTreeNodeId.get(directNodeId) || "";
      if (!directRowId || !eligibleEntityIds.has(directRowId)) return;

      const name = String(node?.name || "").trim();
      nestedEntityOptions.push({
        v: `node:${nodeId}`,
        label: `${name || "Entity"} (interm.)`,
        relatedShareholderRowId: directRowId,
        relatedShareholderNodeId: nodeId,
      });
    });

    return {
      byId,
      eligibleEntityIds,
      entityNameById,
      eligibleTF,
      tfNameById,
      nestedEntityOptions,
    };
  }, [shareholderSignature]);

  /* =========================================================
           VISIBILITY RULES (UI ONLY)
        ========================================================= */

  const showShareholdersBlock =
    showDirectShareholders && !isQuoted && !accountHolderIsTF;

  const shCounts = useMemo(() => {
    const byType = {
      individual: 0,
      entity: 0,
      trust: 0,
      foundation: 0,
      empty: 0,
    };
    (shareholders || []).forEach((s) => {
      const t = normalizeType(s?.holderType);
      if (!t) byType.empty += 1;
      else if (t === "individual") byType.individual += 1;
      else if (t === "entity") byType.entity += 1;
      else if (t === "trust") byType.trust += 1;
      else if (t === "foundation") byType.foundation += 1;
    });
    return byType;
  }, [shareholderSignature]);

  const onlyIndividuals =
    (shCounts.individual > 0 || shareholders.length > 0) &&
    shCounts.entity === 0 &&
    shCounts.trust === 0 &&
    shCounts.foundation === 0;

  const hasNonQuotedEntityShareholder = useMemo(() => {
    return (shareholders || []).some((s) => {
      const t = normalizeType(s?.holderType);
      if (t === "trust" || t === "foundation") return true;
      if (t !== "entity") return false;
      return !isQuotedEntityType(s);
    });
  }, [shareholderSignature]);

  const shouldShowControllingPersonsBase = useMemo(() => {
    if (isQuoted) return false;
    if (accountHolderIsTF) {
      if (isAccountHolderTrust && !hasResolvedAccountHolderTrustName)
        return false;
      return true;
    }

    if (onlyIndividuals && !hasOtherControl) return false;

    if (isPIC) return hasNonQuotedEntityShareholder || hasOtherControl;

    return hasOtherControl || hasNonQuotedEntityShareholder;
  }, [
    isQuoted,
    accountHolderIsTF,
    isAccountHolderTrust,
    hasResolvedAccountHolderTrustName,
    onlyIndividuals,
    hasOtherControl,
    isPIC,
    shareholderSignature,
    hasNonQuotedEntityShareholder,
  ]);

  const shouldShowControllingPersons = useMemo(() => {
    if (isQuoted) return false;
    if (accountHolderIsTF) return shouldShowControllingPersonsBase;
    return (
      shouldShowControllingPersonsBase ||
      (ubos || []).length > 0 ||
      (legalArrangements || []).length > 0
    );
  }, [
    isQuoted,
    accountHolderIsTF,
    shouldShowControllingPersonsBase,
    ubos,
    legalArrangements,
  ]);

  const showNaturalPersons = useMemo(() => {
    if (!shouldShowControllingPersons) return false;
    if (accountHolderIsTF) return false;
    return hasOtherControl || (ubos || []).length > 0;
  }, [shouldShowControllingPersons, accountHolderIsTF, hasOtherControl, ubos]);

  const showLegalArrangements = useMemo(() => {
    if (!shouldShowControllingPersons) return false;
    if (accountHolderIsTF) {
      if (isAccountHolderTrust) return hasResolvedAccountHolderTrustName;
      return true;
    }
    return (
      (legalArrangements || []).length > 0 ||
      shCounts.trust > 0 ||
      shCounts.foundation > 0
    );
  }, [
    shouldShowControllingPersons,
    accountHolderIsTF,
    isAccountHolderTrust,
    hasResolvedAccountHolderTrustName,
    legalArrangements,
    shCounts,
  ]);

  const allowAddButtons = useMemo(() => {
    if (accountHolderIsTF) return false;
    if (!shouldShowControllingPersons) return false;
    return shareholderModel.eligibleEntityIds.size > 0;
  }, [accountHolderIsTF, shouldShowControllingPersons, shareholderModel]);

  const relatedEntityOptions = useMemo(() => {
    const opts = [{ v: "", label: "Select" }];
    if (hasOtherControl)
      opts.push({ v: "__other__", label: "Other form of control" });

    Array.from(shareholderModel.eligibleEntityIds).forEach((id) => {
      const nm = shareholderModel.entityNameById.get(id) || "";
      opts.push({
        v: id,
        label: nm || "Entity",
        relatedShareholderRowId: id,
        relatedShareholderNodeId: "",
      });
    });

    (shareholderModel.nestedEntityOptions || []).forEach((opt) => {
      opts.push(opt);
    });

    return opts;
  }, [hasOtherControl, shareholderModel]);

  const relatedEntityOptionsEntitiesOnly = useMemo(() => {
    const opts = [{ v: "", label: "Select" }];
    Array.from(shareholderModel.eligibleEntityIds).forEach((id) => {
      const nm = shareholderModel.entityNameById.get(id) || "";
      opts.push({
        v: id,
        label: nm || "Entity",
        relatedShareholderRowId: id,
        relatedShareholderNodeId: "",
      });
    });
    (shareholderModel.nestedEntityOptions || []).forEach((opt) => {
      opts.push(opt);
    });
    return opts;
  }, [shareholderModel]);

  /* =========================================================
           SETTERS
        ========================================================= */

  function setLegalArrangements(nextArrangements) {
    if (readOnly) return;
    onSetAt?.(["ownership", "legalArrangements"], {
      arrangements: nextArrangements || [],
    });
  }

  function clearAssistantSnapshot() {
    if (readOnly) return;
    if (!ownership?.assistantTree) return;
    onSetAt?.(["ownership", "assistantTree"], null);
  }

  function onManualOwnershipSetAt(path, value) {
    if (readOnly) return;
    const fieldName = Array.isArray(path) ? path[1] : "";
    const isOwnershipTableEdit = ["shareholders", "ubos", "controllingPersonsNatural", "legalArrangements"].includes(fieldName);
    if (!isOwnershipTableEdit) clearAssistantSnapshot();
    onSetAt?.(path, value);
    // Keep the second copy of an edited list identical (see withOwnershipMirrors).
    const mirrorKey = Array.isArray(path) && path.length === 2 ? OWNERSHIP_MIRROR_KEYS[fieldName] : "";
    if (mirrorKey && ownership && mirrorKey in ownership) onSetAt?.(["ownership", mirrorKey], value);
  }

  function resolveAccountHolderTrustName(kind) {
    if (readOnly) return;
    const normalizedKind = normalizeTrustNameResolution(kind);
    if (!normalizedKind || !entityNameRaw) return;
    clearAssistantSnapshot();
    onSetAt?.(["ownership", "accountHolderTrustResolution"], {
      kind: normalizedKind,
      sourceName: entityNameRaw,
    });
  }

  /* =========================================================
           SINGLE SYNC ENGINE
        ========================================================= */

  useEffect(() => {
    if (readOnly) return;

    if (isQuoted) {
      if (shareholders.length) onSetAt?.(["ownership", "shareholders"], []);
      if (ubos.length) onSetAt?.(["ownership", "ubos"], []);
      if (legalArrangements.length)
        onSetAt?.(["ownership", "legalArrangements"], { arrangements: [] });

      if (uboFlags?.hasOtherControl) {
        const flags = safeClone(uboFlags);
        flags.hasOtherControl = false;
        onSetAt?.(["ownership", "uboFlags"], flags);
      }
      return;
    }

    if (accountHolderIsTF) {
      if (shareholders.length) onSetAt?.(["ownership", "shareholders"], []);
      if (ubos.length) onSetAt?.(["ownership", "ubos"], []);

      const la = ensureLegalArrangementsShape(ownership);
      const curArr = Array.isArray(la.arrangements) ? la.arrangements : [];
      const targetType = accountHolderTFType;
      const requiresTrustResolution =
        targetType === "Trust" && !hasResolvedAccountHolderTrustName;

      if (requiresTrustResolution) {
        const nextArr = curArr.filter(
          (a) =>
            !(
              !!a?.autoCreated &&
              String(a?.source || "") === "account_holder" &&
              normalizeArrangementType(a?.arrangementType) === "Trust"
            )
        );

        const curArrSig = arrangementsSig(curArr);
        const nextArrSig = arrangementsSig(nextArr);
        if (curArrSig !== nextArrSig) setLegalArrangements(nextArr);
        return;
      }

      const idx = curArr.findIndex(
        (a) => !!a?.autoCreated && String(a?.source || "") === "account_holder"
      );

      let nextArr = curArr.slice();

      if (idx === -1) {
        let created = makeArrangement({
            type: targetType,
            name: "",
            source: "account_holder",
            sourceRefRowId: "",
            autoCreated: true,
            relatedShareholderName: entityName,
          });

        if (targetType === "Trust") {
          created = applyAccountHolderTrustResolutionToArrangement(
            created,
            accountHolderTrustResolution,
            entityNameRaw
          );
        } else if (targetType === "Foundation") {
          created = applyAccountHolderFoundationNameToArrangement(
            created,
            entityNameRaw
          );
        }

        nextArr = [created];
      } else {
        const keep = curArr[idx];
        nextArr = curArr.filter((_, i) => i === idx);

        let updated = { ...(keep || {}) };

        if (normalizeArrangementType(updated?.arrangementType) !== targetType) {
          updated = rebuildArrangementForType(updated, targetType);
        }

        updated.source = "account_holder";
        updated.autoCreated = true;
        updated.relatedShareholderName = entityName;

        if (targetType === "Trust") {
          updated = applyAccountHolderTrustResolutionToArrangement(
            updated,
            accountHolderTrustResolution,
            entityNameRaw
          );
        } else if (targetType === "Foundation") {
          updated = applyAccountHolderFoundationNameToArrangement(
            updated,
            entityNameRaw
          );
        }

        nextArr[0] = updated;
      }

      const curArrSig = arrangementsSig(curArr);
      const nextArrSig = arrangementsSig(nextArr);
      if (curArrSig !== nextArrSig) setLegalArrangements(nextArr);

      return;
    }

    const { eligibleEntityIds, entityNameById, eligibleTF, tfNameById } =
      shareholderModel;

    const curUbos = Array.isArray(ownership?.ubos) ? ownership.ubos : [];
    const nextUbos = (curUbos || []).filter((u) => {
      const rid = String(u?.relatedShareholderRowId || "").trim();
      if (!rid) return true;
      if (rid === "__other__") return !!hasOtherControl;
      return eligibleEntityIds.has(rid);
    });

    const la = ensureLegalArrangementsShape(ownership);
    const curArr = Array.isArray(la.arrangements) ? la.arrangements : [];
    let nextArr = curArr.slice();

    nextArr = nextArr.filter((a) => {
      const src = String(a?.source || "");
      const rid = String(a?.sourceRefRowId || "").trim();
      const auto = !!a?.autoCreated;
      if (auto && src === "shareholder") {
        return (rid && eligibleTF.has(rid)) || !!a?._treeArrangementNodeId;
      }
      return true;
    });

    eligibleTF.forEach((targetType, shId) => {
      const idx = nextArr.findIndex(
        (a) =>
          !!a?.autoCreated &&
          String(a?.source || "") === "shareholder" &&
          String(a?.sourceRefRowId || "") === String(shId)
      );

      const shName = tfNameById.get(shId) || "";

      if (idx === -1) {
        nextArr.push(
          makeArrangement({
            type: targetType,
            name: shName,
            source: "shareholder",
            sourceRefRowId: shId,
            autoCreated: true,
            relatedShareholderName: entityName,
          })
        );
        return;
      }

      const cur = nextArr[idx] || {};
      let updated = { ...cur };

      if (normalizeArrangementType(updated?.arrangementType) !== targetType) {
        updated = rebuildArrangementForType(updated, targetType);
      }

      if (
        String(updated?.relatedShareholderName || "") !==
        String(entityName || "")
      ) {
        updated.relatedShareholderName = entityName;
      }

      updated.source = "shareholder";
      updated.sourceRefRowId = shId;
      updated.autoCreated = true;
      updated.name = shName;
      updated._autoName = shName;

      nextArr[idx] = updated;
    });

    nextArr = nextArr.filter((a) => {
      const src = String(a?.source || "");
      if (src !== "entity_manual") return true;
      const rid = String(a?.sourceRefRowId || "").trim();
      if (!rid) return true;
      return eligibleEntityIds.has(rid);
    });

    nextArr = nextArr.map((a) => {
      const src = String(a?.source || "");
      if (src !== "entity_manual") return a;
      if (String(a?.sourceRefNodeId || "").trim()) return a;
      const rid = String(a?.sourceRefRowId || "").trim();
      if (!rid) return a;
      const nm = entityNameById.get(rid) || "";
      if (String(a?.relatedShareholderName || "") === String(nm || ""))
        return a;
      return { ...(a || {}), relatedShareholderName: nm };
    });

    const curUbosSig = ubosSig(curUbos);
    const nextUbosSig = ubosSig(nextUbos);
    if (curUbosSig !== nextUbosSig) onSetAt?.(["ownership", "ubos"], nextUbos);

    const curArrSig = arrangementsSig(curArr);
    const nextArrSig = arrangementsSig(nextArr);
    if (curArrSig !== nextArrSig) setLegalArrangements(nextArr);

    // eslint-disable-next-line
  }, [
    readOnly,
    isQuoted,
    accountHolderIsTF,
    modeKey,
    hasOtherControl,
    shareholderSignature,
    isPIC,
    accountHolderTrustResolution.kind,
    accountHolderTrustResolution.sourceName,
    hasResolvedAccountHolderTrustName,
    entityNameRaw,
  ]);

  /* -------------------- UI -------------------- */

  const effectiveSnapshot = assistantSnapshotOverride?.mermaid ? assistantSnapshotOverride : ownership?.assistantTree;
  const hasAssistantSnapshot = !!effectiveSnapshot?.mermaid;
  const invalidPercentageEdges = useMemo(
    () => assistantSnapshotWarning === OWNERSHIP_PERCENTAGE_WARNING
      ? invalidOwnershipPercentageEdges(effectiveSnapshot) : [],
    [assistantSnapshotWarning, effectiveSnapshot]
  );

  return (
    <div
      className={
        embedded
          ? "kycOwnershipEmbedded"
          : "card kycCard mb-3"
      }
    >
      {removalDialog}
      {!embedded ? (
        <div className="kycSectionHead">
        <div className="d-flex align-items-start justify-content-between gap-2 flex-wrap">
          <div>
            {copy.header.kicker ? (
              <div className="kycSectionKicker">{copy.header.kicker}</div>
            ) : null}

            <h5 className="kycSectionTitle mb-0">
              {sectionNo}
              {copy.header.title}
            </h5>

            {copy.header.hint ? (
              <div className="kycSectionHint">{copy.header.hint}</div>
            ) : null}
          </div>
        </div>
      </div>
      ) : null}

      <div className="kycSectionBody kycOwnershipTables">
        {/* ------------------------------- */}
        {/* ------ Ownership Assistant UI --- */}
        {/* ------------------------------- */}
        {!hideOwnershipAssistantIntro ? (
          <div
            className="CardOwnershipAssistant"
            style={{ marginTop: 5, marginBottom: 20 }}
          >
            <div className="d-flex align-items-center justify-content-between flex-wrap">
              <span className="oaText">
                Complete the ownership details below, or use the assistant to
                auto-populate shareholders and controlling persons:
              </span>

              <button
                type="button"
                className="btn btn-sm btn-ownership-assistant"
                onClick={openOwnershipAssistant}
                disabled={readOnly}
              >
                <i className="bi bi-magic me-2" aria-hidden="true" />
                Open Ownership Assistant
              </button>
            </div>
          </div>
        ) : null}

        {/* ===================== OWNERSHIP ASSISTANT MODAL ===================== */}
        {createPortal(
              <div
                className={`kycOwnershipAssistantModalOverlay${ownershipAssistantOpen ? " isOpen" : ""}`}
                onMouseDown={(e) => {
                  if (e.target === e.currentTarget) closeOwnershipAssistant();
                }}
              >
                <div
                  className="kycOwnershipAssistantModalPanel"
                  onMouseDown={(e) => e.stopPropagation()}
                >
                <div
                  style={{
                    padding: "12px 14px",
                    borderBottom: "1px solid rgba(15, 23, 42, 0.08)",
                    background: "#D2C1B7",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 10,
                  }}
                >
                  <div
                    style={{
                      fontFamily: "Roboto",
                      fontWeight: 300,
                      fontSize: 28,
                      color: "#fff",
                      lineHeight: 1,
                      marginLeft: 20,
                    }}
                  >
                    <i
                      className="bi bi-diagram-3-fill"
                      style={{ fontSize: 28 }}
                    />{" "}
                    <span>Ownership Assistant</span>
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {onHowItWorks ? (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-light kycOwnershipHeaderBtn"
                        onClick={(e) => {
                          e.stopPropagation();
                          onHowItWorks?.();
                        }}
                      >
                        How it works
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-light kycOwnershipHeaderBtn"
                      onClick={closeOwnershipAssistant}
                      aria-label="Close"
                      title="Close"
                    >
                      <i className="bi bi-x-lg" />
                    </button>
                  </div>
                </div>

                <div
                  style={{
                    padding: 16,
                    flex: 1,
                    minHeight: 0,
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    position: "relative",
                  }}
                >
                  <div
                    className="kycOwnershipAssistantBuilderShell"
                    style={{
                      flex: 1,
                      minHeight: 0,
                      overflow: "auto",
                      width: "100%",
                      maxWidth: "none",
                      background: ownershipAssistantContentReady
                        ? "#ffffff"
                        : "#f8f7f5",
                      borderRadius: 14,
                      border: ownershipAssistantContentReady
                        ? "1px solid rgba(15,23,42,0.08)"
                        : "1px solid transparent",
                      boxShadow: ownershipAssistantContentReady
                        ? "0 10px 26px rgba(15,23,42,0.08)"
                        : "none",
                      padding: 14,
                    }}
                  >
                    {ownershipAssistantContentReady ? (
                      <OwnershipTreeUae
                        separateNames={separateNames}
                        orgJurisdiction={orgJurisdiction}
                        customerName={form?.customer?.name}
                        onClose={closeOwnershipAssistant}
                        key={editSeed ? "edit" : "new"}
                        onApplyKycUpdate={applyBuilderUpdate}
                        initialStructure={editSeed}
                        documentShareholders={documentShareholders}
                        initialAccountHolderType={assistantInitialAccountHolderType}
                        initialAccountHolderTrustNameKind={
                          assistantInitialAccountHolderTrustNameKind
                        }
                        clientMode={mode === "client" && !forceApiCallInTree}
                      />
                    ) : null}
                  </div>
                </div>
                </div>
              </div>
        , document.body)}

        {/* ===================== EXISTING UI (UNCHANGED) ===================== */}

        {!hidePreliminaryInformation ? (
          <>
            <div className="OwnershipMainSectionTitle">Preliminary Information</div>
            <div className="kycOwnSubHint">
              Tick only if applicable. If none apply, leave all boxes unchecked.
            </div>

            <div className="mb-3" style={{ marginTop: 8 }}>
              <div className="d-flex flex-column gap-2">
            <div className="d-flex align-items-center gap-2">
              <input
                type="checkbox"
                className="form-check-input m-0"
                disabled={readOnly}
                checked={!!uboFlags.pctNotMeaningful}
                onChange={(e) => {
                  if (readOnly) return;
                  clearAssistantSnapshot();
                  const flags = safeClone(uboFlags);
                  if (e.target.checked) {
                    flags.pctNotMeaningful = true;
                    flags.quotedCompany = false;
                    flags.accountHolderTrust = false;
                    flags.accountHolderFoundation = false;
                  } else {
                    flags.pctNotMeaningful = false;
                  }
                  onManualOwnershipSetAt(["ownership", "uboFlags"], flags);
                }}
              />
              <span className="kycOwnHint">
                {entityName} is a{" "}
                <strong>{ownershipLabel("personal investment company (PIC)")}</strong> or a{" "}
                <strong>passive holding</strong> vehicle primarily used to hold
                investments and assets
              </span>
            </div>

            <div className="d-flex align-items-center gap-2">
              <input
                type="checkbox"
                className="form-check-input m-0"
                disabled={readOnly}
                checked={!!uboFlags.quotedCompany}
                onChange={(e) => {
                  if (readOnly) return;
                  clearAssistantSnapshot();
                  const flags = safeClone(uboFlags);
                  if (e.target.checked) {
                    flags.quotedCompany = true;
                    flags.pctNotMeaningful = false;
                    flags.accountHolderTrust = false;
                    flags.accountHolderFoundation = false;
                  } else {
                    flags.quotedCompany = false;
                  }
                  onManualOwnershipSetAt(["ownership", "uboFlags"], flags);
                }}
              />
              <span className="kycOwnHint">
                {entityName} is a{" "}
                <strong>company quoted on the stock exchange</strong>
              </span>
            </div>

            <div className="d-flex align-items-center gap-2">
              <input
                type="checkbox"
                className="form-check-input m-0"
                disabled={readOnly}
                checked={!!uboFlags.accountHolderTrust}
                onChange={(e) => {
                  if (readOnly) return;
                  clearAssistantSnapshot();
                  const flags = safeClone(uboFlags);
                  if (e.target.checked) {
                    flags.accountHolderTrust = true;
                    flags.accountHolderFoundation = false;
                    flags.pctNotMeaningful = false;
                    flags.quotedCompany = false;
                  } else {
                    flags.accountHolderTrust = false;
                    onManualOwnershipSetAt(
                      ["ownership", "accountHolderTrustResolution"],
                      null
                    );

                    const la = ensureLegalArrangementsShape(ownership);
                    const list = Array.isArray(la.arrangements)
                      ? la.arrangements
                      : [];
                    const nextArrangements = list.map((arr) => {
                      if (
                        !arr?.autoCreated ||
                        String(arr?.source || "") !== "account_holder" ||
                        normalizeArrangementType(arr?.arrangementType) !== "Trust"
                      ) {
                        return arr;
                      }
                      return clearAccountHolderTrustResolutionFromArrangement(
                        arr
                      );
                    });
                    onManualOwnershipSetAt(["ownership", "legalArrangements"], {
                      arrangements: nextArrangements,
                    });
                  }
                  onManualOwnershipSetAt(["ownership", "uboFlags"], flags);
                }}
              />
              <span className="kycOwnHint">
                {entityName} is a <strong>trust</strong>
              </span>
            </div>

            <div className="d-flex align-items-center gap-2">
              <input
                type="checkbox"
                className="form-check-input m-0"
                disabled={readOnly}
                checked={!!uboFlags.accountHolderFoundation}
                onChange={(e) => {
                  if (readOnly) return;
                  clearAssistantSnapshot();
                  const flags = safeClone(uboFlags);
                  if (e.target.checked) {
                    flags.accountHolderFoundation = true;
                    flags.accountHolderTrust = false;
                    flags.pctNotMeaningful = false;
                    flags.quotedCompany = false;
                  } else {
                    flags.accountHolderFoundation = false;
                  }
                  onManualOwnershipSetAt(["ownership", "uboFlags"], flags);
                }}
              />
              <span className="kycOwnHint">
                {entityName} is a <strong>foundation</strong>
              </span>
            </div>
              </div>
            </div>
          </>
        ) : null}

        {needsAccountHolderTrustResolution ? (
          <div className="alert alert-warning py-3 mb-4">
            <div className="fw-semibold mb-1">
              You marked this account holder as a trust.
            </div>
            <div className="small mb-2">
              Does the current client name <b>{entityNameRaw}</b> refer to the
              trustee or to the trust?
            </div>
            <div className="d-flex gap-2 flex-wrap">
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => resolveAccountHolderTrustName("trustee")}
              >
                This is trustee name
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => resolveAccountHolderTrustName("trust")}
              >
                This is trust name
              </button>
            </div>
          </div>
        ) : null}

        {isQuoted ? (
          <div className="alert alert-success mb-4 kycOwnAlertGreen kycOwnQuotedFinalState">
            <span className="kycOwnQuotedFinalIcon">
              <i className="bi bi-check2-circle" aria-hidden="true" />
            </span>
            <div>
              <strong>No further ownership details required</strong>
              <p>
                Direct shareholders and controlling persons are not required because{" "}
                {entityName} is a <b>quoted company</b>.
              </p>
            </div>
          </div>
        ) : null}

        {/* ===================== OWNERSHIP TREE SNAPSHOT ===================== */}

        {/* Ownership Structure Diagram SNAPSHOT */}
        <AnimatePresence initial={false}>
          {hasAssistantSnapshot && !hideAssistantSnapshot ? (
            <motion.div
              key="assistant-diagram"
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
              style={{ overflow: "hidden" }}
            >
              <hr
                className="opacity-25"
                style={{ marginTop: 15, marginBottom: 25 }}
              />
              <div className="OwnershipMainSectionTitle">
                Ownership Structure Diagram
              </div>
              <div style={{ marginTop: 16 }} />
              <div
                className="CardOwnershipAssistantPreview"
                style={{ marginTop: 0, marginBottom: 20 }}
              >
                <OwnershipTreeUaeReadOnly
                  snapshot={effectiveSnapshot}
                  validationWarning={assistantSnapshotWarning}
                  invalidPercentageEdges={invalidPercentageEdges}
                  edgeLabelBg="#fffcfb"
                  onEditPercentage={!readOnly && onSetAt ? (edge, value) => {
                    onSetAt(["ownership"], editOwnershipPercentage(ownership, effectiveSnapshot, edge, value));
                  } : undefined}
                />
                {assistantSnapshotWarning ? (
                  <div className="CardOwnershipAssistantPreviewWarning">
                    <img src={warningIcon} alt="" aria-hidden="true" />
                    <span>{assistantSnapshotWarning}</span>
                  </div>
                ) : null}
                {!readOnly && onSetAt && effectiveSnapshot?.nodes?.length > 1 ? (
                  <button
                    type="button"
                    className="kycOwnershipDiagramEditBtn"
                    onClick={() => openEditDiagram(effectiveSnapshot)}
                    title="Edit the ownership structure in the visual builder"
                  >
                    <i className="bi bi-pencil" aria-hidden="true" /> Edit
                  </button>
                ) : null}
              </div>
            </motion.div>
          ) : null}
        </AnimatePresence>

        {/* ===================== DIRECT SHAREHOLDER SECTION ===================== */}

        {showDirectShareholders && !isQuoted && !accountHolderIsTF ? (
          <>
            {!embedded ? (
              <hr
                className="opacity-25"
                style={{ marginTop: 20, marginBottom: 20 }}
              />
            ) : null}

            <DirectShareholdersTable
              titleNote={shareholdersNote}
              hideDateOfBirth={clientFacing}
              separateNames={separateNames}
              readOnly={readOnly}
              entityName={entityName}
              shareholders={shareholders}
              prefilledRowIds={(Array.isArray(shareholders) ? shareholders : []).filter((row) => isPrefilledRow(form?.customer, "shareholders", row)).map((row) => row._rowId)}
              individualsOnly={ownership?.desktopClientStructureType === "direct"}
              legalArrangements={legalArrangements}
              onSetAt={onManualOwnershipSetAt}
              hidePercentColumn={!!isPIC}
              clientMode={mode === "client"}
              changedRowIds={changedRows?.shareholders || []}
              qualifiedNodeIds={qualifiedUboNodeIds}
              wrapClassName={
                mode !== "client" && cc(["ownership", "shareholders"])
                  ? "kycChangedWrap"
                  : ""
              }
            />

            {/* hidden in client mode — keep for staff/review */}
            {mode !== "client" ? (
              <div className="mb-3">
                <div className="d-flex align-items-center gap-2">
                  <input
                    type="checkbox"
                    className="form-check-input m-0"
                    disabled={readOnly}
                    checked={!!uboFlags.hasOtherControl}
                    onChange={(e) => {
                      if (readOnly) return;
                      clearAssistantSnapshot();
                      const flags = safeClone(uboFlags);
                      flags.hasOtherControl = e.target.checked;
                      onManualOwnershipSetAt(["ownership", "uboFlags"], flags);

                      if (e.target.checked) {
                        const cur = Array.isArray(ownership?.ubos)
                          ? ownership.ubos
                          : [];
                        if (cur.length === 0) {
                          const row = {
                            _rowId: makeRowId("ubo"),
                            relatedShareholderRowId: "__other__",
                            name: "",
                            nationality: "",
                            countryOfResidence: "",
                            basisOfControl: "",
                          };
                          onManualOwnershipSetAt(["ownership", "ubos"], [row]);
                        }
                      }
                    }}
                  />
                  <span className="kycOwnHint">
                    Tick if there is any other form of control by individual(s)
                    other than direct shareholding (e.g. shareholder agreements).
                  </span>
                </div>
              </div>
            ) : null}
          </>
        ) : null}

        {!isQuoted && shouldShowControllingPersons ? (
          <>
            {!hideControllingPersonsDivider ? (
              <hr
                style={{ marginTop: 25, marginBottom: 20, borderColor: "rgba(15, 23, 42, 0.08)", opacity: 1 }}
              />
            ) : null}
            <ControllingPersonsSection
              separateNames={separateNames}
              hideDateOfBirth={clientFacing}
              readOnly={readOnly}
              ownership={ownership}
              ownershipSnapshot={effectiveSnapshot}
              onOwnershipPercentageChange={!readOnly && onSetAt ? (edge, value) => {
                onSetAt(["ownership"], editOwnershipPercentage(ownership, effectiveSnapshot, edge, value));
              } : undefined}
              onSetAt={onManualOwnershipSetAt}
              showNaturalPersons={
                !accountHolderIsTF &&
                (hasOtherControl || (ubos || []).length > 0)
              }
              showLegalArrangements={
                showLegalArrangements ||
                (legalArrangements || []).length > 0 ||
                (shareholders || []).some((s) => {
                  const t = normalizeType(s?.holderType);
                  return t === "trust" || t === "foundation";
                })
              }
              allowAddButtons={
                !accountHolderIsTF &&
                shareholderModel.eligibleEntityIds.size > 0
              }
              relatedEntityOptions={relatedEntityOptions}
              relatedEntityOptionsEntitiesOnly={
                relatedEntityOptionsEntitiesOnly
              }
              entityName={entityName}
              accountHolderIsTF={accountHolderIsTF}
              accountHolderTFType={accountHolderTFType}
              accountHolderTrustResolutionKind={accountHolderTrustResolution.kind}
              hideSectionHeadings={hideControllingPersonsHeadings}
              naturalPersonsWrapClassName={
                mode !== "client" && cc(["ownership", "ubos"])
                  ? "kycChangedWrap"
                  : ""
              }
              changedUboRowIds={changedRows?.ubos || []}
              qualifiedNodeIds={qualifiedUboNodeIds}
              clientMode={mode === "client"}
            />
          </>
        ) : null}

        {submitAttempted && mode === "client" && (() => {
          const shIncomplete = shareholders.some((r) => {
            if (!String(r?.name || "").trim()) return true;
            const holderType = normalizeOwnershipHolderType(r?.holderType);
            if (
              holderType === "trust" ||
              holderType === "foundation" ||
              holderType === "legalArrangement"
            ) {
              return !isLegalArrangementShareholderComplete(r, legalArrangements);
            }
            if (!String(r?.countryOfResidence || "").trim()) return true;
            const t = String(r?.holderType || "").toLowerCase();
            if (t === "individual" && !String(r?.nationality || "").trim()) return true;
            return false;
          });
          const uboIncomplete = ubos.some((r) =>
            !isUboRowComplete(r, legalArrangements)
          );
          if (!shIncomplete && !uboIncomplete) return null;
          return (
            <div style={{ marginTop: 16 }}>
              <div className="kycInlineFieldError kycInlineFieldError--centered">
                <i className="bi bi-exclamation-triangle-fill" aria-hidden="true" />{" "}
                Please complete all rows (name, nationality and domicile are required)
              </div>
            </div>
          );
        })()}

        {showSignatories ? (
          <>
            <hr
              className="opacity-25"
              style={{ marginTop: 25, marginBottom: 20 }}
            />
            <SignatoriesTable
              readOnly={readOnly}
              signatories={signatories}
              onSetAt={onManualOwnershipSetAt}
              wrapClassName={
                cc(["ownership", "signatories"]) ? "kycChangedWrap" : ""
              }
            />
          </>
        ) : null}
      </div>
    </div>
  );
}
